import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { Zap, Skull, Loader2, ChevronDown, Sparkles } from 'lucide-react';
import { CINEMATIC_KEYFRAMES, ScanlineOverlay, NoiseOverlay, GasParticles, ToxicDrips, SewerFrame, GlitchTitle, StatusBar, TotalFeesCounter } from './CinematicStyles';
import { useDemo } from '../../context/DemoContext';
import { toast } from 'sonner';
import OnChainReceipt, { fireOnChainToast } from '../OnChainReceipt';

import brokenCoinImg from '../../assets/slots/broken_coin.png';
import gasCanisterImg from '../../assets/slots/gas_canister_slot.png';
import maskedGremlinImg from '../../assets/slots/masked_gremlin.png';
import aetherSymbolImg from '../../assets/slots/aether_symbol.png';
import vaultDoorImg from '../../assets/slots/vault_door.png';
import ancientCrownImg from '../../assets/slots/ancient_crown.png';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SYMBOLS = [
  { id: 0, name: 'Broken Coin', color: '#c9a227', image: brokenCoinImg, payout: [1.5, 3, 8] },
  { id: 1, name: 'Gas Canister', color: '#22c55e', image: gasCanisterImg, payout: [2, 5, 12] },
  { id: 2, name: 'Masked Gremlin', color: '#ff8c00', image: maskedGremlinImg, payout: [3, 8, 20] },
  { id: 3, name: 'Aether Symbol', color: '#22c55e', image: aetherSymbolImg, payout: [5, 15, 35] },
  { id: 4, name: 'Vault Door', color: '#eab308', image: vaultDoorImg, payout: [8, 25, 50] },
  { id: 5, name: 'Ancient Crown', color: '#f59e0b', image: ancientCrownImg, payout: [15, 50, 100] },
];

const BET_OPTIONS = [
  { amount: 10000, label: 'Small', color: '#ff8c00' },
  { amount: 50000, label: 'Medium', color: '#ffd700' },
  { amount: 100000, label: 'High', color: '#ff4500' },
];

const Reel = ({ symbolId, spinning }) => {
  const symbol = SYMBOLS[symbolId];
  const [showResult, setShowResult] = useState(!spinning);

  useEffect(() => {
    if (spinning) setShowResult(false);
    else { const timer = setTimeout(() => setShowResult(true), 50); return () => clearTimeout(timer); }
  }, [spinning]);

  return (
    <div className="relative w-16 h-20 sm:w-20 sm:h-24 md:w-28 md:h-32 lg:w-32 lg:h-36 overflow-hidden"
      style={{
        // Reliquary window — brass-bound stone aperture
        background: `
          radial-gradient(ellipse at 50% 100%, rgba(255,69,0,0.18) 0%, transparent 55%),
          linear-gradient(180deg, rgba(0,0,0,0.92) 0%, rgba(20,10,4,0.85) 50%, rgba(0,0,0,0.92) 100%)
        `,
        border: '1px solid rgba(180,140,55,0.55)',
        boxShadow: `
          inset 0 0 16px rgba(0,0,0,0.7),
          inset 0 1px 1px rgba(220,170,80,0.18),
          inset 0 -1px 1px rgba(0,0,0,0.8),
          0 0 0 2px rgba(20,12,5,0.95),
          0 0 0 3px rgba(140,100,40,0.55),
          0 2px 6px rgba(0,0,0,0.6)
        `,
        borderRadius: '3px',
        ...(spinning ? { animation: 'reelArcaneGlow 0.8s ease-in-out infinite' } : {}),
      }}>
      {/* Brass corner studs */}
      {[
        { top: 2, left: 2 }, { top: 2, right: 2 },
        { bottom: 2, left: 2 }, { bottom: 2, right: 2 },
      ].map((p, i) => (
        <div key={i} className="absolute pointer-events-none" style={{
          ...p,
          width: 4, height: 4,
          borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 30%, rgba(220,180,90,0.85) 0%, rgba(110,75,25,0.95) 50%, rgba(40,25,8,1) 100%)',
          boxShadow: 'inset 0 0.5px 0.5px rgba(255,200,100,0.4), 0 0.5px 1px rgba(0,0,0,0.6)',
          zIndex: 3,
        }} />
      ))}
      {spinning ? (
        <div className="absolute inset-0 flex flex-col items-center" style={{ animation: 'slotSpin 0.12s linear infinite' }}>
          {[...SYMBOLS, ...SYMBOLS, ...SYMBOLS].map((s, i) => (
            <div key={i} className="flex-shrink-0 w-full flex items-center justify-center"
                 style={{ height: '100%', opacity: 0.35, filter: 'blur(2px)' }}>
              <img src={s.image} alt="" className="w-10 h-10 sm:w-12 sm:h-12 md:w-20 md:h-20 object-contain" draggable={false} />
            </div>
          ))}
        </div>
      ) : showResult ? (
        <motion.div className="absolute inset-0 flex items-center justify-center p-2"
          initial={{ y: -50, opacity: 0, scale: 0.6 }} animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.05 }}>
          <img src={symbol.image} alt={symbol.name} className="w-10 h-10 sm:w-14 sm:h-14 md:w-22 md:h-22 lg:w-24 lg:h-24 object-contain" draggable={false}
            style={{ filter: `drop-shadow(0 0 8px ${symbol.color}60) drop-shadow(0 1px 2px rgba(0,0,0,0.8))` }} />
        </motion.div>
      ) : null}
      {/* Top + bottom shadow gradients for depth */}
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, transparent 18%, transparent 82%, rgba(0,0,0,0.45) 100%)' }} />
    </div>
  );
};

export default function SlotMachine({ onWin, onBack }) {
  const { publicKey, connected: walletConnected } = useWallet();
  const { isDemo, demoWallet, isLive } = useDemo();
  const connected = walletConnected || isDemo;
  const walletAddress = publicKey?.toBase58() || (isDemo ? demoWallet : undefined);
  
  const [gameState, setGameState] = useState('menu');
  const [reels, setReels] = useState([0, 1, 2, 1, 0]);
  const [targetReels, setTargetReels] = useState([0, 1, 2, 1, 0]);
  const [reelSpinning, setReelSpinning] = useState([false, false, false, false, false]);
  const [betAmount, setBetAmount] = useState(10000);
  const [lastWin, setLastWin] = useState(0);
  const [winType, setWinType] = useState('');
  const [onchainReceipt, setOnchainReceipt] = useState(null);
  const [specialEvent, setSpecialEvent] = useState('');
  
  const [gasBalance, setGasBalance] = useState(0);
  const [totalWon, setTotalWon] = useState(0);
  const [biggestWin, setBiggestWin] = useState(0);
  const [vaultMeter, setVaultMeter] = useState(0);
  const [freeSpins, setFreeSpins] = useState(0);
  const [holderFreeSpins, setHolderFreeSpins] = useState(null); // { tier, remaining, monthly_total, tier_color }
  const [showFreeSpinPopup, setShowFreeSpinPopup] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [manualStopIndex, setManualStopIndex] = useState(0);
  const [autoStopTimer, setAutoStopTimer] = useState(null);
  const [totalFeesEarned, setTotalFeesEarned] = useState(0);
  const targetReelsRef = useRef([0, 1, 2, 1, 0]);
  const stoppedReelsRef = useRef([false, false, false, false, false]);
  const innerTimersRef = useRef([]);
  const freeSpinPopupShown = useRef(false);
  const lastSpinDataRef = useRef(null);
  
  useEffect(() => { if (walletAddress) fetchUserData(); }, [walletAddress]);
  
  const fetchUserData = async () => {
    if (!walletAddress) return;
    try {
      const res = await fetch(`${API_URL}/api/slots/user/${walletAddress}`);
      if (res.ok) {
        const data = await res.json();
        setGasBalance(data.fee_balance || 0);
        setTotalWon(data.total_won || 0);
        setBiggestWin(data.biggest_win || 0);
        setVaultMeter(data.vault_meter || 0);
        setFreeSpins(data.free_spins || 0);
        if (data.holder_free_spins) {
          setHolderFreeSpins(data.holder_free_spins);
          // Show popup on first load if they have free spins
          if (data.holder_free_spins.remaining > 0 && !freeSpinPopupShown.current) {
            freeSpinPopupShown.current = true;
            setTimeout(() => setShowFreeSpinPopup(true), 800);
          }
        }
      }
    } catch (err) { console.error('Failed to fetch slot data:', err); }
  };

  const autoClaimFees = useCallback(async (winAmount, winTypeStr) => {
    if (!walletAddress || winAmount <= 0) return;
    setTotalFeesEarned(prev => prev + winAmount);
    try {
      await fetch(`${API_URL}/api/arcade/score`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: walletAddress, game_type: 'slot_machine', fees_earned: winAmount, game_data: { win_type: winTypeStr, bet_amount: betAmount } }),
      });
    } catch (err) { console.error('Auto-claim failed:', err); }
  }, [walletAddress, betAmount]);
  
  const startSpin = useCallback((newTargetReels) => {
    setReelSpinning([true, true, true, true, true]);
    setLastWin(0); setWinType(''); setSpecialEvent('');
    setGameState('spinning'); setManualStopIndex(0);
    targetReelsRef.current = newTargetReels;
    stoppedReelsRef.current = [false, false, false, false, false];
    innerTimersRef.current.forEach(t => clearTimeout(t));
    innerTimersRef.current = [];
    const timer = setTimeout(() => {
      for (let i = 0; i < 5; i++) {
        const inner = setTimeout(() => {
          if (stoppedReelsRef.current[i]) return; // already manually stopped
          stoppedReelsRef.current[i] = true;
          setReels(prev => { const u = [...prev]; u[i] = targetReelsRef.current[i]; return u; });
          setReelSpinning(prev => { const u = [...prev]; u[i] = false; return u; });
        }, i * 300);
        innerTimersRef.current.push(inner);
      }
    }, 5000);
    setAutoStopTimer(timer);
  }, []);
  
  const stopNextReel = useCallback(() => {
    if (gameState !== 'spinning') return;
    const idx = manualStopIndex;
    if (idx >= 5) return;
    if (stoppedReelsRef.current[idx]) { setManualStopIndex(idx + 1); return; }
    stoppedReelsRef.current[idx] = true;
    setReels(prev => { const u = [...prev]; u[idx] = targetReelsRef.current[idx]; return u; });
    setReelSpinning(prev => { const u = [...prev]; u[idx] = false; return u; });
    setManualStopIndex(idx + 1);
    if (idx === 4) {
      if (autoStopTimer) { clearTimeout(autoStopTimer); setAutoStopTimer(null); }
      innerTimersRef.current.forEach(t => clearTimeout(t));
      innerTimersRef.current = [];
    }
  }, [gameState, manualStopIndex, autoStopTimer]);

  useEffect(() => {
    const allStopped = reelSpinning.every(s => !s);
    if (gameState === 'spinning' && allStopped && manualStopIndex >= 5) {
      if (autoStopTimer) { clearTimeout(autoStopTimer); setAutoStopTimer(null); }
      innerTimersRef.current.forEach(t => clearTimeout(t));
      innerTimersRef.current = [];
      const serverData = lastSpinDataRef.current;
      lastSpinDataRef.current = null;
      if (serverData) {
        setGasBalance(serverData.new_balance);
        setVaultMeter(serverData.vault_meter);
        if (serverData.onchain) {
          setOnchainReceipt(serverData.onchain);
          fireOnChainToast(toast, serverData.onchain);
        }
        if (serverData.special_event) {
          setSpecialEvent(serverData.special_event);
          if (serverData.free_spins_awarded > 0) setFreeSpins(prev => prev + serverData.free_spins_awarded);
        }
        if (serverData.is_win && serverData.win_amount > 0) {
          setLastWin(serverData.win_amount); setWinType(serverData.win_type);
          setTotalWon(prev => prev + serverData.win_amount);
          if (serverData.win_amount > biggestWin) setBiggestWin(serverData.win_amount);
          setGameState('won');
          if (onWin) onWin(serverData.win_amount, { reels: serverData.reels, winType: serverData.win_type });
          autoClaimFees(serverData.win_amount, serverData.win_type);
        } else { setGameState('ready'); }
      } else {
        // Fallback: server data missing, detect win client-side from target reels
        const t = targetReelsRef.current;
        const counts = {};
        for (let i = 0; i < 5; i++) { counts[t[i]] = (counts[t[i]] || 0) + 1; }
        let bestSymbol = -1, bestCount = 0;
        for (const [sym, count] of Object.entries(counts)) {
          if (count >= 3 && count > bestCount) { bestSymbol = parseInt(sym); bestCount = count; }
        }
        if (bestCount >= 3) {
          const payouts = [[1.5, 3, 8], [2, 5, 12], [3, 8, 20], [5, 15, 35], [8, 25, 50], [15, 50, 100]];
          const multiplier = payouts[bestSymbol]?.[bestCount - 3] || 1;
          const win = Math.floor(betAmount * multiplier);
          setLastWin(win); setWinType(bestCount === 5 ? '5 of a Kind - FULL MATCH!' : bestCount === 4 ? '4 of a Kind' : '3 of a Kind');
          setTotalWon(prev => prev + win);
          if (win > biggestWin) setBiggestWin(win);
          setGameState('won');
          autoClaimFees(win, bestCount === 5 ? '5 of a Kind - FULL MATCH!' : bestCount === 4 ? '4 of a Kind' : '3 of a Kind');
        } else { setGameState('ready'); }
      }
    }
  }, [reelSpinning, gameState, manualStopIndex, betAmount, autoStopTimer, biggestWin, onWin, autoClaimFees]);

  useEffect(() => {
    const allStopped = reelSpinning.every(s => !s);
    if (gameState === 'spinning' && allStopped && manualStopIndex < 5) setManualStopIndex(5);
  }, [reelSpinning, gameState, manualStopIndex]);

  
  const spin = useCallback(async () => {
    if (!walletAddress || gameState === 'spinning') return;
    if (gameState === 'menu') setGameState('ready');
    // Refresh balance before spinning to catch any recent game earnings
    try {
      const balRes = await fetch(`${API_URL}/api/slots/user/${walletAddress}`);
      if (balRes.ok) {
        const balData = await balRes.json();
        setGasBalance(balData.fee_balance || 0);
        if ((balData.fee_balance || 0) < betAmount) return; // Still insufficient after refresh
      }
    } catch (e) { /* proceed with cached balance */ }
    if (gasBalance < betAmount) return;
    try {
      const res = await fetch(`${API_URL}/api/slots/spin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: walletAddress, bet_amount: betAmount, use_free_spin: false, live_mode: !!isLive }),
      });
      const data = await res.json();
      if (!data.success) { setGameState('ready'); return; }
      setGasBalance(data.new_balance);
      setTargetReels(data.reels);
      startSpin(data.reels);
      lastSpinDataRef.current = data;
    } catch (err) { console.error('Spin failed:', err); setGameState('ready'); }
  }, [walletAddress, gasBalance, betAmount, gameState, startSpin]);

  const useFreeSpin = useCallback(async () => {
    if (!walletAddress || gameState === 'spinning' || !holderFreeSpins || holderFreeSpins.remaining <= 0) return;
    if (gameState === 'menu') setGameState('ready');
    try {
      const res = await fetch(`${API_URL}/api/slots/spin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: walletAddress, bet_amount: betAmount, use_free_spin: true, live_mode: !!isLive }),
      });
      const data = await res.json();
      if (!data.success) { setGameState('ready'); return; }
      setGasBalance(data.new_balance);
      setHolderFreeSpins(prev => prev ? { ...prev, remaining: data.free_spins_remaining } : prev);
      setTargetReels(data.reels);
      startSpin(data.reels);
      lastSpinDataRef.current = data;
    } catch (err) { console.error('Free spin failed:', err); setGameState('ready'); }
  }, [walletAddress, betAmount, gameState, holderFreeSpins, startSpin]);
  
  const isDisabled = !connected || gasBalance < betAmount || gameState === 'spinning';
  const hasFreeSpins = holderFreeSpins && holderFreeSpins.remaining > 0;

  // ============ CINEMATIC MENU STATE ============
  if (gameState === 'menu') {
    return (
      <div className="relative h-screen w-full overflow-hidden select-none" data-testid="slot-machine-menu"
        style={{ background: 'radial-gradient(ellipse at 50% 60%, #1a0d05 0%, #0d0700 30%, #050300 100%)' }}>
        <style>{CINEMATIC_KEYFRAMES}{`
          @keyframes slotSpin { 0% { transform: translateY(0); } 100% { transform: translateY(-33.33%); } }
          @keyframes neonPulse {
            0%, 100% { box-shadow: 0 0 8px rgba(255,140,0,0.4), 0 0 20px rgba(255,140,0,0.2); border-color: rgba(255,140,0,0.4); }
            50% { box-shadow: 0 0 15px rgba(232,121,249,0.5), 0 0 35px rgba(255,140,0,0.3); border-color: rgba(232,121,249,0.5); }
          }
          @keyframes vaultPulse {
            0%, 100% { box-shadow: 0 0 20px rgba(255,140,0,0.2), 0 0 40px rgba(255,140,0,0.1), inset 0 0 15px rgba(255,140,0,0.05); }
            50% { box-shadow: 0 0 35px rgba(255,140,0,0.3), 0 0 60px rgba(255,140,0,0.15), inset 0 0 25px rgba(255,140,0,0.08); }
          }
        `}</style>
        <ScanlineOverlay />
        <NoiseOverlay />
        <GasParticles count={16} color="molten" />
        <ToxicDrips count={5} color="#ff8c00" />

        {/* Purple sewer frame */}
        <div className="absolute top-0 left-0 right-0 h-1 z-30" style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,140,0,0.3) 20%, rgba(255,140,0,0.1) 50%, rgba(255,140,0,0.3) 80%, transparent 100%)',
        }} />
        <div className="absolute bottom-0 left-0 right-0 h-1 z-30" style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,140,0,0.2) 20%, rgba(255,140,0,0.05) 50%, rgba(255,140,0,0.2) 80%, transparent 100%)',
        }} />
        <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-orange-400/40 z-30" />
        <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-orange-400/40 z-30" />
        <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-orange-400/40 z-30" />
        <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-orange-400/40 z-30" />

        {/* Vault circle background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.7, rotate: -30 }}
            animate={{ opacity: 0.08, scale: 1, rotate: 0 }}
            transition={{ duration: 2.5 }}
            className="w-72 h-72 md:w-96 md:h-96 rounded-full"
            style={{ border: '2px solid rgba(255,140,0,0.2)', animation: 'vaultPulse 4s ease-in-out infinite' }}
          >
            <div className="absolute inset-6 rounded-full border border-orange-400/10" />
            <div className="absolute inset-12 rounded-full border border-orange-400/5" />
            {[...Array(12)].map((_, i) => (
              <div key={i} className="absolute w-px h-5" style={{
                left: '50%', top: '4px',
                transformOrigin: '50% calc(50% + 170px)',
                transform: `translateX(-50%) rotate(${i * 30}deg)`,
                background: `rgba(255,140,0,${i % 3 === 0 ? 0.2 : 0.06})`,
              }} />
            ))}
          </motion.div>
        </div>

        <div className="relative z-20 flex flex-col items-center justify-center h-full px-4">

          <GlitchTitle text="FEE SCRAMBLER" subtitle="ancient vault awaits" color="#ff8c00" />

          {/* Symbol preview row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex gap-3 mt-8"
          >
            {SYMBOLS.slice(0, 5).map((s, i) => (
              <motion.div key={s.id}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8 + i * 0.1, type: 'spring' }}
                className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center"
                style={{
                  background: 'rgba(0,0,0,0.5)',
                  border: `1px solid ${s.color}25`,
                  borderRadius: '4px',
                }}
              >
                <img src={s.image} alt={s.name} className="w-8 h-8 md:w-10 md:h-10 object-contain" style={{ filter: 'brightness(0.7)' }} />
              </motion.div>
            ))}
          </motion.div>

          {/* Info blocks */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="flex gap-4 mt-6"
          >
            <div className="text-center px-4 py-2" style={{
              background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,140,0,0.15)', borderRadius: '2px',
            }}>
              <p className="text-[9px] tracking-wider uppercase" style={{ color: 'rgba(255,140,0,0.4)', fontFamily: "'Courier New', monospace" }}>Reels</p>
              <p className="text-lg font-bold" style={{ color: '#ff8c00', fontFamily: "'Courier New', monospace" }}>5</p>
            </div>
            <div className="text-center px-4 py-2" style={{
              background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(232,121,249,0.15)', borderRadius: '2px',
            }}>
              <p className="text-[9px] tracking-wider uppercase" style={{ color: 'rgba(232,121,249,0.4)', fontFamily: "'Courier New', monospace" }}>Symbols</p>
              <p className="text-lg font-bold" style={{ color: '#ffd700', fontFamily: "'Courier New', monospace" }}>6</p>
            </div>
            <div className="text-center px-4 py-2" style={{
              background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(245,158,11,0.15)', borderRadius: '2px',
            }}>
              <p className="text-[9px] tracking-wider uppercase" style={{ color: 'rgba(245,158,11,0.4)', fontFamily: "'Courier New', monospace" }}>Max Reward</p>
              <p className="text-lg font-bold" style={{ color: '#f59e0b', fontFamily: "'Courier New', monospace" }}>100x</p>
            </div>
          </motion.div>

          {/* Enter Button */}
          {connected ? (
            <motion.button
              onClick={() => setGameState('ready')}
              data-testid="enter-vault-btn"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5 }}
              className="mt-8 px-10 py-4 font-bold text-base tracking-[0.15em] uppercase relative overflow-hidden"
              style={{
                background: 'rgba(0,0,0,0.5)',
                border: '1px solid rgba(255,140,0,0.4)',
                color: '#ff8c00',
                borderRadius: '2px',
                fontFamily: "'Courier New', monospace",
                boxShadow: '0 0 15px rgba(255,140,0,0.2), 0 0 30px rgba(255,140,0,0.1)',
              }}
              whileHover={{ scale: 1.02, boxShadow: '0 0 25px rgba(255,140,0,0.4), 0 0 50px rgba(255,140,0,0.2)' }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="relative z-10 flex items-center gap-2">
                <Zap className="w-5 h-5" /> APPROACH THE VAULT
              </span>
              <motion.div className="absolute inset-0"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(255,140,0,0.08), transparent)' }}
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              />
            </motion.button>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
              className="mt-8 text-center space-y-4"
            >
              <p className="text-sm" style={{ color: 'rgba(232,121,249,0.5)', fontFamily: "'Courier New', monospace" }}>Connect wallet to access the vault</p>
              <WalletMultiButton className="!bg-orange-500/10 !border !border-orange-500/25 !rounded-sm !py-3 !px-8" />
            </motion.div>
          )}

          <StatusBar text="VAULT INTEGRITY: COMPROMISED // SCRAMBLER ONLINE" color="#ff8c00" />
        </div>
      </div>
    );
  }

  // ============ PLAYING / READY / SPINNING / WON / LOST STATES ============
  return (
    <div className="relative h-screen w-full overflow-hidden"
         style={{ background: 'radial-gradient(ellipse at 50% 40%, #1a0d05 0%, #0d0700 40%, #050300 100%)' }}>
      
      <style>{CINEMATIC_KEYFRAMES}{`
        @keyframes slotSpin { 0% { transform: translateY(0); } 100% { transform: translateY(-33.33%); } }
        @keyframes neonPulse {
          0%, 100% { box-shadow: 0 0 8px rgba(255,140,0,0.4), 0 0 20px rgba(255,140,0,0.2), inset 0 0 15px rgba(255,140,0,0.1); border-color: rgba(255,140,0,0.4); }
          50% { box-shadow: 0 0 15px rgba(232,121,249,0.5), 0 0 35px rgba(255,140,0,0.3), inset 0 0 20px rgba(232,121,249,0.12); border-color: rgba(232,121,249,0.5); }
        }
        @keyframes reelArcaneGlow {
          0%, 100% {
            box-shadow:
              inset 0 0 16px rgba(0,0,0,0.7),
              inset 0 1px 1px rgba(220,170,80,0.18),
              inset 0 -1px 1px rgba(0,0,0,0.8),
              0 0 0 2px rgba(20,12,5,0.95),
              0 0 0 3px rgba(180,140,55,0.7),
              0 0 14px rgba(255,140,0,0.4);
          }
          50% {
            box-shadow:
              inset 0 0 22px rgba(255,140,0,0.18),
              inset 0 1px 1px rgba(255,210,100,0.32),
              inset 0 -1px 1px rgba(0,0,0,0.8),
              0 0 0 2px rgba(20,12,5,0.95),
              0 0 0 3px rgba(255,180,70,0.85),
              0 0 24px rgba(255,140,0,0.65);
          }
        }
        @keyframes ashFall {
          0% { transform: translateY(-10px); opacity: 0; }
          15% { opacity: 0.8; }
          100% { transform: translateY(220px); opacity: 0; }
        }
        @keyframes crestPulse {
          0%, 100% { filter: drop-shadow(0 0 8px rgba(255,140,0,0.5)); }
          50% { filter: drop-shadow(0 0 16px rgba(255,180,80,0.85)); }
        }
      `}</style>
      <ScanlineOverlay />
      <NoiseOverlay />
      <GasParticles count={12} color="molten" />

      {/* HUD Top Bar — Centered */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-center py-3 px-2"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 60%, transparent 100%)' }}>
        <div className="flex items-center gap-3 md:gap-6">
          <div className="text-center">
            <span className="text-[8px] md:text-[9px] tracking-[0.2em] uppercase block font-bold" style={{ color: '#ffc870', fontFamily: "'Courier New', monospace", textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>BALANCE</span>
            <span className="text-xl md:text-2xl font-black tracking-wide" style={{ color: '#ff8c00', fontFamily: "'Impact', 'Arial Black', sans-serif", textShadow: '0 0 12px rgba(255,140,0,0.35)' }}>{gasBalance.toLocaleString()}</span>
          </div>
          <div className="w-px h-8" style={{ background: 'rgba(255,140,0,0.12)' }} />
          <div className="text-center">
            <span className="text-[8px] md:text-[9px] tracking-[0.2em] uppercase block font-bold" style={{ color: '#f0a8ff', fontFamily: "'Courier New', monospace", textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>WON</span>
            <span className="text-xl md:text-2xl font-black tracking-wide" style={{ color: '#ffd700', fontFamily: "'Impact', 'Arial Black', sans-serif", textShadow: '0 0 12px rgba(232,121,249,0.35)' }}>{totalWon.toLocaleString()}</span>
          </div>
          <div className="w-px h-8" style={{ background: 'rgba(255,140,0,0.12)' }} />
          <div className="text-center">
            <span className="text-[8px] md:text-[9px] tracking-[0.2em] uppercase block font-bold" style={{ color: '#ffd86b', fontFamily: "'Courier New', monospace", textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>BEST</span>
            <span className="text-xl md:text-2xl font-black tracking-wide" style={{ color: '#f59e0b', fontFamily: "'Impact', 'Arial Black', sans-serif", textShadow: '0 0 12px rgba(245,158,11,0.35)' }}>{biggestWin.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center h-full px-4 pt-12">
        
        {/* Vault Surge Meter */}
        <div className="w-full max-w-xl mb-3">
          <div className="flex items-center justify-between text-xs mb-1" style={{ fontFamily: "'Courier New', monospace" }}>
            <span className="text-[9px] tracking-widest uppercase" style={{ color: 'rgba(255,140,0,0.3)' }}>VAULT SURGE</span>
            <span className="font-bold" style={{ color: 'rgba(232,121,249,0.5)' }}>{Math.floor(vaultMeter * 100)}%</span>
          </div>
          <div className="h-1.5 overflow-hidden" style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,140,0,0.1)', borderRadius: '1px' }}>
            <motion.div className="h-full" style={{ background: 'linear-gradient(90deg, #cc5500, #ffd700)', boxShadow: '0 0 10px rgba(255,140,0,0.4)' }}
              animate={{ width: `${vaultMeter * 100}%` }} />
          </div>
        </div>
        
        {/* REELS — Gothic Reliquary Cabinet */}
        <div className="relative mb-4" data-testid="slot-reels">
          {/* ═══ Stone-bound reliquary cabinet wrapping the reels ═══ */}
          <div className="relative" style={{ padding: '28px 36px 20px' }}>
            {/* Outer stone shell */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: `
                radial-gradient(ellipse at 50% 100%, rgba(255,69,0,0.10) 0%, transparent 55%),
                linear-gradient(180deg, rgba(20,12,4,0.85) 0%, rgba(8,4,2,0.95) 100%)
              `,
              border: '1px solid rgba(140,100,40,0.4)',
              boxShadow: `
                inset 0 1px 1px rgba(220,170,80,0.15),
                inset 0 -2px 4px rgba(0,0,0,0.7),
                inset 0 0 30px rgba(0,0,0,0.5),
                0 0 0 2px rgba(8,4,2,0.95),
                0 0 0 3px rgba(180,140,55,0.45),
                0 6px 22px rgba(0,0,0,0.7),
                0 0 32px rgba(255,140,0,0.10)
              `,
              borderRadius: 3,
            }} />

            {/* Top brass crest with rune */}
            <div className="absolute pointer-events-none" style={{
              top: -16, left: '50%',
              transform: 'translateX(-50%)',
              width: 92, height: 32,
              animation: 'crestPulse 3.2s ease-in-out infinite',
            }}>
              <svg viewBox="0 0 92 32" width="92" height="32">
                <defs>
                  <linearGradient id="crestBrass" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#f0d68a" />
                    <stop offset="35%" stopColor="#c69544" />
                    <stop offset="70%" stopColor="#7a5018" />
                    <stop offset="100%" stopColor="#3a2208" />
                  </linearGradient>
                </defs>
                {/* Crest plate — pointed gothic shield */}
                <path d="M 8 4 L 84 4 L 80 22 L 46 30 L 12 22 Z" fill="url(#crestBrass)" stroke="#8b5e1c" strokeWidth="0.8" />
                {/* Inner rune mark */}
                <text x="46" y="20" textAnchor="middle" fontFamily="'Cinzel', serif" fontSize="11" fontWeight="900" fill="#1a0d04" letterSpacing="2">FEES</text>
                {/* Side rivets */}
                <circle cx="14" cy="13" r="1.5" fill="#3a2208" />
                <circle cx="78" cy="13" r="1.5" fill="#3a2208" />
              </svg>
            </div>

            {/* Carved stone columns — left + right */}
            {['left', 'right'].map((side) => (
              <div key={side} className="absolute top-2 bottom-2 pointer-events-none" style={{
                [side]: 4,
                width: 14,
                background: `
                  linear-gradient(90deg, rgba(180,140,55,0.45) 0%, rgba(60,40,15,0.95) 25%, rgba(30,18,6,1) 50%, rgba(60,40,15,0.95) 75%, rgba(180,140,55,0.45) 100%)
                `,
                border: '1px solid rgba(180,140,55,0.5)',
                boxShadow: 'inset 0 1px 1px rgba(220,170,80,0.25), inset 0 -1px 1px rgba(0,0,0,0.7)',
              }}>
                {/* Column capital - top */}
                <div className="absolute -top-1 -left-1 -right-1 h-2" style={{
                  background: 'linear-gradient(180deg, rgba(220,170,80,0.7) 0%, rgba(120,80,30,0.9) 100%)',
                  border: '1px solid rgba(180,140,55,0.65)',
                  boxShadow: 'inset 0 1px 1px rgba(255,200,100,0.4)',
                }} />
                {/* Column base - bottom */}
                <div className="absolute -bottom-1 -left-1 -right-1 h-2" style={{
                  background: 'linear-gradient(180deg, rgba(120,80,30,0.9) 0%, rgba(220,170,80,0.7) 100%)',
                  border: '1px solid rgba(180,140,55,0.65)',
                  boxShadow: 'inset 0 -1px 1px rgba(255,200,100,0.4)',
                }} />
              </div>
            ))}

            {/* Brass corner caps — 4 ornate studs */}
            {[
              { top: 6, left: 6 }, { top: 6, right: 6 },
              { bottom: 6, left: 6 }, { bottom: 6, right: 6 },
            ].map((p, i) => (
              <div key={i} className="absolute pointer-events-none" style={{
                ...p,
                width: 10, height: 10,
                borderRadius: '50%',
                background: 'radial-gradient(circle at 35% 30%, rgba(220,180,90,0.95) 0%, rgba(140,95,30,0.95) 50%, rgba(40,25,8,1) 100%)',
                boxShadow: 'inset 0 1px 1.5px rgba(255,220,140,0.5), inset 0 -1px 1px rgba(0,0,0,0.7), 0 1px 2px rgba(0,0,0,0.7)',
                zIndex: 4,
              }} />
            ))}

            {/* Ash particles drifting down inside the cabinet */}
            {[...Array(6)].map((_, i) => (
              <div key={i} className="absolute pointer-events-none" style={{
                top: 10,
                left: `${15 + i * 13}%`,
                width: 2, height: 2,
                borderRadius: '50%',
                background: i % 2 === 0 ? 'rgba(220,180,90,0.55)' : 'rgba(255,140,40,0.6)',
                boxShadow: i % 2 === 0 ? '0 0 3px rgba(220,180,90,0.5)' : '0 0 4px rgba(255,140,40,0.55)',
                animation: `ashFall ${3 + (i * 0.4)}s ${i * 0.5}s linear infinite`,
                zIndex: 1,
              }} />
            ))}

            {/* Reels themselves — kept inside the cabinet */}
            <div className="relative flex justify-center gap-2 md:gap-3 z-10">
              {reels.map((symbolId, index) => (
                <Reel key={index} symbolId={symbolId} spinning={reelSpinning[index]} />
              ))}
            </div>

            {/* Molten ember light bleeding from beneath the reels */}
            <div className="absolute pointer-events-none" style={{
              left: 30, right: 30, bottom: 6, height: 8,
              background: 'radial-gradient(ellipse at 50% 100%, rgba(255,80,0,0.55) 0%, rgba(255,140,0,0.25) 35%, transparent 70%)',
              filter: 'blur(2px)',
              zIndex: 2,
            }} />
          </div>

          <AnimatePresence>
            {specialEvent === 'vault_surge' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden" style={{ borderRadius: '2px' }}>
                <motion.p className="font-black text-2xl tracking-widest" style={{ color: '#ffd700', fontFamily: "'Courier New', monospace", textShadow: '0 0 20px rgba(232,121,249,0.6)' }}
                  animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 0.5, repeat: 3 }}>
                  VAULT SURGE!
                </motion.p>
              </motion.div>
            )}
            {specialEvent === 'chancellor_glare' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: [0, 0.8, 0] }} transition={{ duration: 1.5 }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ background: 'rgba(239,68,68,0.15)', borderRadius: '2px' }}>
                <Skull className="w-20 h-20" style={{ color: 'rgba(239,68,68,0.6)' }} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Stake Selector */}
        <div className="flex gap-2 mb-4">
          {BET_OPTIONS.map((option) => (
            <button key={option.amount} onClick={() => setBetAmount(option.amount)}
              disabled={gameState === 'spinning'} data-testid={`bet-${option.amount}`}
              className="transition-all"
              style={{
                background: betAmount === option.amount ? `${option.color}12` : 'rgba(0,0,0,0.4)',
                border: `1px solid ${betAmount === option.amount ? `${option.color}50` : 'rgba(255,255,255,0.04)'}`,
                borderRadius: '2px', padding: '8px 16px',
                color: betAmount === option.amount ? option.color : 'rgba(255,255,255,0.25)',
                fontFamily: "'Courier New', monospace",
              }}>
              <span className="block text-[9px] opacity-50 tracking-wider">{option.label.toUpperCase()}</span>
              <span className="text-base font-bold">{option.amount.toLocaleString()}</span>
            </button>
          ))}
        </div>
        
        {/* Spin / Stop Button */}
        {gameState === 'spinning' ? (
          <motion.button onClick={stopNextReel} data-testid="stop-reel-btn"
            className="px-12 py-3 font-black text-lg tracking-wider"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)', color: '#ef4444', borderRadius: '2px', fontFamily: "'Courier New', monospace" }}
            whileTap={{ scale: 0.95 }}
            animate={{ scale: [1, 1.02, 1] }} transition={{ duration: 0.6, repeat: Infinity }}>
            STOP ({5 - manualStopIndex} LEFT)
          </motion.button>
        ) : connected && gasBalance >= betAmount ? (
          <motion.button onClick={spin} data-testid="spin-btn"
            className="px-12 py-3 font-black text-lg tracking-wider relative overflow-hidden"
            style={{
              background: 'rgba(255,140,0,0.08)',
              border: '1px solid rgba(255,140,0,0.3)',
              color: '#ff8c00', borderRadius: '2px', fontFamily: "'Courier New', monospace",
            }}
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
            <span className="relative z-10 flex items-center justify-center gap-2">
              <Zap className="w-5 h-5" />SCRAMBLE ({betAmount.toLocaleString()})
            </span>
            <motion.div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,140,0,0.06), transparent)' }}
              animate={{ x: ['-100%', '100%'] }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }} />
          </motion.button>
        ) : (
          <div className="text-center space-y-3">
            {connected ? (
              <p className="text-sm py-3 px-6" style={{ color: 'rgba(232,121,249,0.5)', fontFamily: "'Courier New', monospace",
                background: 'rgba(232,121,249,0.04)', border: '1px solid rgba(232,121,249,0.15)', borderRadius: '2px' }}>
                Need {betAmount.toLocaleString()} fees to scramble
              </p>
            ) : (
              <>
                <p className="text-sm" style={{ color: 'rgba(232,121,249,0.4)', fontFamily: "'Courier New', monospace" }}>
                  Connect wallet to play
                </p>
                <WalletMultiButton className="!bg-orange-500/10 !border !border-orange-500/25 !rounded-sm !py-2 !px-6" />
              </>
            )}
          </div>
        )}

        {/* FREE SPIN BUTTON — Holder Privilege */}
        {connected && hasFreeSpins && gameState !== 'spinning' && (
          <motion.button
            onClick={useFreeSpin}
            data-testid="free-spin-btn"
            className="mt-2 px-10 py-3 font-black text-base tracking-wider relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(255,140,0,0.12) 0%, rgba(245,158,11,0.06) 100%)',
              border: '1px solid rgba(255,140,0,0.4)',
              color: '#fbbf24',
              borderRadius: '4px',
              fontFamily: "'Courier New', monospace",
              boxShadow: '0 0 20px rgba(255,140,0,0.15), inset 0 1px 0 rgba(255,140,0,0.2)',
            }}
            whileHover={{ scale: 1.03, boxShadow: '0 0 30px rgba(255,140,0,0.25)' }}
            whileTap={{ scale: 0.97 }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5" />FREE SPIN ({holderFreeSpins.remaining} left)
            </span>
            <motion.div className="absolute inset-0"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,140,0,0.08), transparent)' }}
              animate={{ x: ['-100%', '100%'] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }} />
          </motion.button>
        )}
        
        {/* Back to Menu */}
        {gameState !== 'spinning' && (
          <button onClick={() => setGameState('menu')} data-testid="back-to-menu-btn"
            className="mt-3 flex items-center gap-1 transition-colors"
            style={{ color: 'rgba(255,140,0,0.3)', fontFamily: "'Courier New', monospace", fontSize: '11px', letterSpacing: '0.1em' }}>
            {'<'} BACK TO MENU
          </button>
        )}

        {/* Rules Toggle + Side Panel */}
        <button onClick={() => setShowRules(!showRules)} data-testid="rules-toggle"
          className="mt-2 flex items-center gap-1 transition-colors"
          style={{ color: 'rgba(255,140,0,0.3)', fontFamily: "'Courier New', monospace", fontSize: '11px', letterSpacing: '0.1em' }}>
          <ChevronDown className={`w-3 h-3 transition-transform ${showRules ? 'rotate-180' : ''}`} />
          {showRules ? 'HIDE' : 'SHOW'} REWARDS
        </button>
      </div>

      {/* Rules Side Panel */}
      <AnimatePresence>
        {showRules && (
          <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full z-50 w-80 overflow-y-auto"
            style={{
              background: 'linear-gradient(180deg, rgba(20,10,5,0.98) 0%, rgba(8,2,15,0.98) 100%)',
              borderLeft: '2px solid rgba(255,140,0,0.3)',
              backdropFilter: 'blur(16px)',
              boxShadow: '-10px 0 40px rgba(255,140,0,0.1)',
            }}>
            <div className="p-5">
              <div className="flex items-center justify-between mb-5">
                <span className="text-sm font-black tracking-widest uppercase" style={{
                  background: 'linear-gradient(90deg, #ff8c00, #ffd700)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                }}>REWARD TABLE</span>
                <button onClick={() => setShowRules(false)} className="px-3 py-1 text-xs font-bold tracking-wider transition-all"
                  style={{ color: 'rgba(255,140,0,0.6)', border: '1px solid rgba(255,140,0,0.2)', borderRadius: '2px', fontFamily: "'Courier New', monospace" }}>
                  CLOSE
                </button>
              </div>
              <div className="space-y-2.5">
                {SYMBOLS.map((symbol) => (
                  <div key={symbol.id} className="flex items-center gap-3 p-3" style={{
                    background: 'rgba(255,140,0,0.04)',
                    border: '1px solid rgba(255,140,0,0.12)',
                    borderRadius: '4px',
                  }}>
                    <img src={symbol.image} alt={symbol.name} className="w-10 h-10 object-contain flex-shrink-0" style={{ filter: 'drop-shadow(0 0 6px rgba(255,140,0,0.3))' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold mb-1" style={{ color: 'rgba(255,255,255,0.7)', fontFamily: "'Courier New', monospace" }}>{symbol.name}</p>
                      <div className="flex gap-3 text-xs font-bold" style={{ fontFamily: "'Courier New', monospace" }}>
                        <span style={{ color: '#ff8c00' }}>3x <span style={{ color: 'rgba(255,140,0,0.6)' }}>{symbol.payout[0]}x</span></span>
                        <span style={{ color: '#ffd700' }}>4x <span style={{ color: 'rgba(232,121,249,0.6)' }}>{symbol.payout[1]}x</span></span>
                        <span style={{ color: '#f59e0b' }}>5x <span style={{ color: 'rgba(245,158,11,0.6)' }}>{symbol.payout[2]}x</span></span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 space-y-3 p-3" style={{
                background: 'rgba(0,0,0,0.3)',
                border: '1px solid rgba(255,140,0,0.1)',
                borderRadius: '4px',
              }}>
                <p className="text-xs font-bold" style={{ fontFamily: "'Courier New', monospace" }}>
                  <span style={{ color: '#ffd700' }}>VAULT SURGE </span>
                  <span style={{ color: 'rgba(232,121,249,0.5)' }}>— 5% chance — 2x multiplier + Free Spins</span>
                </p>
                <p className="text-xs font-bold" style={{ fontFamily: "'Courier New', monospace" }}>
                  <span style={{ color: '#ef4444' }}>CHANCELLOR </span>
                  <span style={{ color: 'rgba(239,68,68,0.5)' }}>— 3% chance — 5% balance penalty</span>
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <TotalFeesCounter fees={totalFeesEarned} color="#ff8c00" />

      {/* ===== FULL-SCREEN WIN/LOSS CINEMATIC ===== */}
      <AnimatePresence>
        {gameState === 'won' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
            onClick={() => setGameState('ready')}
            data-testid="spin-result-overlay"
          >
            <style>{`
              @keyframes slotVictorySlam {
                0% { transform: scale(4) rotate(-8deg); opacity: 0; letter-spacing: 0.5em; }
                25% { transform: scale(1.1) rotate(2deg); opacity: 1; letter-spacing: 0.12em; }
                45% { transform: scale(0.97) rotate(-1deg); }
                65% { transform: scale(1.02) rotate(0deg); }
                100% { transform: scale(1) rotate(0deg); letter-spacing: 0.15em; opacity: 1; }
              }
              @keyframes slotPulse {
                0%, 100% { text-shadow: 0 0 20px currentColor, 0 0 40px currentColor; }
                50% { text-shadow: 0 0 30px currentColor, 0 0 60px currentColor, 0 0 90px currentColor; }
              }
              @keyframes slotRays {
                0% { opacity: 0; transform: scale(0.3) rotate(0deg); }
                30% { opacity: 0.6; transform: scale(1) rotate(10deg); }
                100% { opacity: 0.08; transform: scale(1.3) rotate(25deg); }
              }
              @keyframes slotGlitch {
                0%, 100% { transform: translate(0); }
                20% { transform: translate(-3px, 2px); }
                40% { transform: translate(2px, -2px); }
                60% { transform: translate(-2px, 1px); }
                80% { transform: translate(1px, -1px); }
              }
              @keyframes countReveal {
                0% { transform: scale(0.3) translateY(20px); opacity: 0; }
                60% { transform: scale(1.1) translateY(-5px); }
                100% { transform: scale(1) translateY(0); opacity: 1; }
              }
            `}</style>

            {gameState === 'won' && (
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="text-center pointer-events-none"
              >
                {/* Radial rays */}
                <div style={{
                  position: 'fixed', inset: '-50%', zIndex: -1,
                  animation: 'slotRays 1.2s ease-out forwards',
                  pointerEvents: 'none',
                }}>
                  {[...Array(20)].map((_, i) => (
                    <div key={i} style={{
                      position: 'absolute', top: '50%', left: '50%',
                      width: '200%', height: '2px',
                      background: `linear-gradient(90deg, transparent 30%, rgba(255,140,0,${0.1 + Math.random() * 0.15}) 50%, transparent 70%)`,
                      transformOrigin: '0% 50%',
                      transform: `rotate(${i * 18}deg)`,
                    }} />
                  ))}
                </div>

                {/* Win type label */}
                <div style={{
                  fontSize: 'clamp(12px, 2.5vw, 18px)',
                  color: '#ffd700',
                  fontFamily: "'Courier New', monospace",
                  letterSpacing: '0.3em',
                  animation: 'slotVictorySlam 0.5s 0.1s cubic-bezier(0.22,1,0.36,1) both',
                  textShadow: '0 0 10px rgba(232,121,249,0.4)',
                  marginBottom: '8px',
                }}>
                  {winType.toUpperCase()}
                </div>

                {/* VAULT CRACKED */}
                <div style={{
                  fontSize: 'clamp(36px, 9vw, 72px)',
                  fontWeight: 900,
                  color: '#ff8c00',
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  animation: 'slotVictorySlam 0.6s 0.15s cubic-bezier(0.22,1,0.36,1) both',
                  textShadow: '0 0 25px rgba(255,140,0,0.5), 0 0 60px rgba(255,140,0,0.2), 0 4px 0 #6b21a8, 0 8px 0 #4c1d95',
                  WebkitTextStroke: '1px #cc5500',
                  letterSpacing: '0.15em',
                  animation: 'slotVictorySlam 0.6s 0.15s cubic-bezier(0.22,1,0.36,1) both, slotGlitch 0.3s 0.8s ease-out',
                }}>
                  VAULT CRACKED
                </div>

                {/* Win amount */}
                <div style={{
                  fontSize: 'clamp(32px, 8vw, 60px)',
                  fontWeight: 900,
                  color: '#22c55e',
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  animation: 'countReveal 0.5s 0.6s cubic-bezier(0.22,1,0.36,1) both',
                  textShadow: '0 0 20px rgba(34,197,94,0.4), 0 0 50px rgba(34,197,94,0.15)',
                  letterSpacing: '0.05em',
                }}>
                  +{lastWin.toLocaleString()}
                </div>

                <div style={{
                  fontSize: '12px',
                  color: 'rgba(34,197,94,0.5)',
                  fontFamily: "'Courier New', monospace",
                  letterSpacing: '0.25em',
                  animation: 'countReveal 0.4s 0.8s cubic-bezier(0.22,1,0.36,1) both',
                }}>
                  FEES EXTRACTED
                </div>

                <div style={{
                  fontSize: '11px',
                  color: '#ff6b00',
                  fontFamily: "'Courier New', monospace",
                  letterSpacing: '0.15em',
                  marginTop: '6px',
                  animation: 'countReveal 0.4s 1.0s cubic-bezier(0.22,1,0.36,1) both',
                }}>
                  +10 SEASON POINTS
                </div>

                {onchainReceipt && (
                  <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', animation: 'countReveal 0.4s 1.2s cubic-bezier(0.22,1,0.36,1) both' }}>
                    <OnChainReceipt receipt={onchainReceipt} compact />
                  </div>
                )}

                {/* Tap to continue */}
                <div style={{
                  marginTop: '32px',
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.2)',
                  fontFamily: "'Courier New', monospace",
                  letterSpacing: '0.2em',
                  animation: 'slotPulse 2s 1.5s ease-in-out infinite both',
                }}>
                  TAP ANYWHERE TO CONTINUE
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <StatusBar text="SCRAMBLER ACTIVE // ENTRY LOCKED" color="#ff8c00" />

      {/* FREE SPINS POPUP — Vibrant holder reward notification */}
      <AnimatePresence>
        {showFreeSpinPopup && holderFreeSpins && holderFreeSpins.remaining > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[500] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
            onClick={() => setShowFreeSpinPopup(false)}
            data-testid="free-spin-popup"
          >
            <motion.div
              initial={{ scale: 0.3, rotateY: 90 }}
              animate={{ scale: 1, rotateY: 0 }}
              exit={{ scale: 0.3, opacity: 0 }}
              transition={{ type: 'spring', damping: 15, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-md w-full mx-4 p-8 text-center"
              style={{
                background: 'radial-gradient(ellipse at top, rgba(255,140,0,0.15) 0%, rgba(20,10,5,0.98) 60%)',
                border: '2px solid rgba(255,140,0,0.4)',
                borderRadius: '16px',
                boxShadow: '0 0 60px rgba(255,140,0,0.2), 0 0 120px rgba(255,140,0,0.08), inset 0 1px 0 rgba(255,140,0,0.3)',
              }}
            >
              {/* Animated sparkle particles */}
              {[...Array(12)].map((_, i) => (
                <motion.div key={i}
                  className="absolute w-1.5 h-1.5 rounded-full"
                  style={{
                    background: i % 3 === 0 ? '#fbbf24' : i % 3 === 1 ? '#f59e0b' : '#eab308',
                    top: `${15 + Math.random() * 70}%`,
                    left: `${10 + Math.random() * 80}%`,
                    boxShadow: `0 0 6px ${i % 2 === 0 ? '#fbbf24' : '#f59e0b'}`,
                  }}
                  animate={{
                    y: [0, -20, 0], opacity: [0.3, 1, 0.3], scale: [0.5, 1.2, 0.5],
                  }}
                  transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
                />
              ))}

              {/* Tier badge */}
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="inline-block px-4 py-1.5 mb-4 rounded-full text-xs font-bold tracking-[0.3em] uppercase"
                style={{
                  background: `${holderFreeSpins.tier_color}15`,
                  border: `1px solid ${holderFreeSpins.tier_color}40`,
                  color: holderFreeSpins.tier_color,
                  boxShadow: `0 0 15px ${holderFreeSpins.tier_color}20`,
                  fontFamily: "'Courier New', monospace",
                }}
              >
                {holderFreeSpins.tier_title || holderFreeSpins.tier}
              </motion.div>

              {/* Main title */}
              <motion.h2
                initial={{ scale: 3, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', damping: 12, delay: 0.5 }}
                className="text-3xl font-black tracking-wider mb-2"
                style={{
                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b, #eab308, #fbbf24)',
                  backgroundSize: '200% 200%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  filter: 'drop-shadow(0 0 15px rgba(255,140,0,0.4))',
                  animation: 'shimmerGold 3s linear infinite',
                }}
              >
                FREE SPINS
              </motion.h2>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="text-sm mb-6"
                style={{ color: 'rgba(255,140,0,0.6)', fontFamily: "'Courier New', monospace" }}
              >
                Holder privilege this month
              </motion.p>

              {/* Big number */}
              <motion.div
                initial={{ scale: 0, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', damping: 10, stiffness: 150, delay: 0.9 }}
                className="relative inline-block mb-6"
              >
                <span className="text-7xl font-black" style={{
                  color: '#fbbf24',
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  textShadow: '0 0 20px rgba(255,140,0,0.5), 0 0 40px rgba(255,140,0,0.25), 0 0 80px rgba(255,140,0,0.1)',
                  animation: 'pulseGlow 2s ease-in-out infinite',
                }}>
                  {holderFreeSpins.remaining}
                </span>
                <div className="absolute -top-2 -right-6">
                  <Sparkles className="w-6 h-6 text-yellow-400" style={{ animation: 'spin 3s linear infinite' }} />
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
                className="text-xs mb-6"
                style={{ color: 'rgba(255,255,255,0.3)', fontFamily: "'Courier New', monospace", lineHeight: '1.6' }}
              >
                No fees deducted on free spins<br />
                Win fees as a reward for holding $GFG<br />
                Resets monthly — keep holding to keep spinning
              </motion.p>

              {/* CTA */}
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 }}
                onClick={() => setShowFreeSpinPopup(false)}
                className="px-10 py-3 font-black text-sm tracking-[0.2em] uppercase relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,140,0,0.15), rgba(245,158,11,0.08))',
                  border: '2px solid rgba(255,140,0,0.5)',
                  color: '#fbbf24',
                  borderRadius: '4px',
                  fontFamily: "'Courier New', monospace",
                  boxShadow: '0 0 20px rgba(255,140,0,0.15)',
                }}
                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(255,140,0,0.3)' }}
                whileTap={{ scale: 0.95 }}
                data-testid="free-spin-popup-close"
              >
                START SCRAMBLING
                <motion.div className="absolute inset-0"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(255,140,0,0.1), transparent)' }}
                  animate={{ x: ['-100%', '100%'] }} transition={{ duration: 1.5, repeat: Infinity }} />
              </motion.button>

              <style>{`
                @keyframes shimmerGold {
                  0% { background-position: 0% 50%; }
                  100% { background-position: 200% 50%; }
                }
                @keyframes pulseGlow {
                  0%, 100% { text-shadow: 0 0 20px rgba(255,140,0,0.5), 0 0 40px rgba(255,140,0,0.25); }
                  50% { text-shadow: 0 0 30px rgba(255,140,0,0.7), 0 0 60px rgba(255,140,0,0.35), 0 0 100px rgba(255,140,0,0.15); }
                }
              `}</style>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
